import Paper from "@mui/material/Paper"
import { useTheme } from "@mui/material/styles";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import useHolidayTableColumns from "./holiday-table-columns";
import { AgGridCustomToolbarProps } from "../../../../util/component/AgGridCustomToolbar";
import ToolbarActions from "./ToolbarActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IHolidayFormFilter, IHolidayRow } from "../types";
import { useRecoilState } from "recoil";
import { formInEditModeState, holidaysState } from "../state";
import { ColDef, FirstDataRenderedEvent, IServerSideDatasource } from "ag-grid-community";
import { deepClone } from "../../../../util/state/commonFunctions";
import HolidayFilter from "../holiday-filter/HolidayFilter";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { editHolidays, getHolidays, insertHoliday } from "../services/http.services";
import { useForm } from "react-hook-form";
import { HOLIDAY_FILTER_DEFAULTS } from "../holiday-filter/const";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import HolidayEditDialog from "../holiday-edit-dialog/HolidayEditDialog";
import CustomLoader from "../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup";

function HolidayTable() {
    const {t} = useTranslation(['network', 'translation']);
    const {t:t2} = useTranslation(['translation']);
    const theme = useTheme();
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);
    const columnDefs = useHolidayTableColumns();

    const tenants = useMemo<string[]>(() => {
        return globalData?.user.legalEntities?.map((elem: any) => elem.name) || [];
    }, [globalData]);

    const [inEditMode, setInEditMode] = useRecoilState(formInEditModeState);
    const [rowData, setRowData] = useRecoilState(holidaysState);
    const rowDataRef = useRef<IHolidayRow[]>();
    const [pages, setPages] = useState<IHolidayRow[][]>([]);

    const [oldRowData, setOldRowData] = useState(rowData);

    const [remountTable, setRemountTable] = useState(0);
    const [filters, setFilters] = useState<IHolidayFormFilter | null>(null);
    const [showHolidayCreation, setShowHolidayCreation] = useState(false);

    const holidaysMutation = useMutation(getHolidays);
    const holidayCreationMutation = useMutation(insertHoliday);
    const holidayEditMutation = useMutation(editHolidays);

    const validationSchema = useMemo(() => yup.object({
        tenants: yup
            .array()
            .transform((value, originalValue) => {
                if (typeof originalValue === 'string' && originalValue.trim() !== "") {
                    return [originalValue];
                }
                return value;
            })
            .min(1, t2("error.required"))
            .required(t2("error.required"))
            .typeError(t2("error.required"))
    }), [t2]);

    const form = useForm<IHolidayFormFilter>({
        defaultValues: HOLIDAY_FILTER_DEFAULTS,
        resolver: yupResolver(validationSchema)
    });
    const formTenant = form.watch("tenants")?.at(0) ?? "";

    function onSearch() {
        form.handleSubmit((data) => {
            setFilters(data);
        })();
    }

    useEffect(() => {
        rowDataRef.current = rowData;
    }, [rowData])

    const onEditSave = useCallback(() => {
        holidayEditMutation.mutateAsync(rowData, {
            onSuccess() {
                setFilters(filters ? { ...filters } : form.getValues()); // Reload the holidays table
                setInEditMode(false);
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t2('success.updateOperation'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            },
            onError(error: any) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message:
                                error?.response?.data?.error === 'DUPLICATE_HOLIDAYS'
                                    ? t('msHoliday.errors.DUPLICATE_HOLIDAYS')
                                    : t2('error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            },
        })
        .catch(() => {});
    }, [rowData]);

    function onEditCancel() {
        setRowData(oldRowData);
        setInEditMode(false);
    }

    // Pass editDisabled so the Edit button is greyed out when the table has no rows
    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderInfo: () => <HolidayFilter form={form} onSearch={onSearch} />,
        renderActions: () => <ToolbarActions onSave={onEditSave} onCancel={onEditCancel} editDisabled={rowData.length === 0} />
    }), [t, rowData, oldRowData]);

    function handleAddHoliday() {
        setShowHolidayCreation(true);
    }

    const defaultColDef = useMemo<ColDef>(() => ({
        autoHeaderHeight: true,
        wrapHeaderText: true,
        resizable: true,
        valueSetter(params) {
            const field = params.colDef.field! as keyof IHolidayRow
            const value = params.newValue

            const dataCopy = [...rowData].map(rd => ({ ...rd })) as any;
            const row = dataCopy.find((row: any) => row.id === params.data.id);

            if (field === '_date') {
                const dateValue = value as Date;
                row.day = dateValue.getDate();
                row.month = dateValue.getMonth() + 1;
                row.year = dateValue.getFullYear();
            } else {
                row[field] = value;
            }

            setRowData(dataCopy);
            return true;
        },
    }), [rowData]);

    const onFirstDataRendered = useCallback((event: FirstDataRenderedEvent) => {
        event.api.sizeColumnsToFit();
    }, []);

    const onGridSizeChanged = useCallback((event: any) => {
        event.api.sizeColumnsToFit();
    }, []);

    useEffect(() => {
        if (inEditMode) {
            setOldRowData(deepClone(rowData));
        }
        setRemountTable(old => old + 1);
    }, [inEditMode])

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                if (!filters) {
                    params.success({ rowCount: 0, rowData: [] });
                    params.api.showNoRowsOverlay();
                    return;
                }

                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                const { sort, colId } = params.request.sortModel[0] ?? {}

                await holidaysMutation.mutateAsync({
                    ...filters,
                    tenants: filters.tenants,
                    paramRequest: {
                        pagination: limit,
                        startIndex: params.request.startRow,
                        endIndex: params.request.endRow,
                        sortBy: sort && {
                            field: colId === "_date" ? "date" : colId,
                            order: sort,
                        },
                    },
                }).then((response: any) => {
                    const holidays = response.holidays.map((holiday: any) => {
                        const holidayClone: IHolidayRow = deepClone(holiday);
                        holidayClone._date = new Date(`${holiday.year}-${holiday.month}-${holiday.day}`);
                        return holidayClone;
                    });
                    setPages(oldPages => {
                        const copy = [...oldPages];
                        const pageId = params.api.paginationGetCurrentPage();
                        while(pageId > copy.length) copy.push([]);
                        copy[pageId] = holidays
                        return copy;
                    });
                    params.success({
                        rowData: holidays,
                        rowCount: response.params.totCount,
                    })
                });
            } catch {
                params.fail();
                params.api.hideOverlay();
            }
        },
    }), [tenants, filters]);

    function handleCreationSave(data: IHolidayRow) {
        return new Promise<boolean>((resolve, reject) => {
            holidayCreationMutation.mutateAsync(data, {
                onSuccess() {
                    setFilters(filters ? { ...filters } : form.getValues()); // Reload the holidays table
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t2('success.createOperation'),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            },
                        },
                    });
                    resolve(true);
                },
                onError(error: any) {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message:
                                    error?.response?.data?.error === 'DUPLICATE_HOLIDAYS'
                                        ? t('msHoliday.errors.DUPLICATE_HOLIDAYS')
                                        : t2('error.tryLater'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            },
                        },
                    });
                    resolve(false);
                },
            });
        })
        .catch(() => false);
    }

    function handleCreationClose() {
        setShowHolidayCreation(false);
    }

    const handlePageChanged = useCallback((num: number) => {
        setRowData(pages[num] ?? []);
    }, [pages])

    return (
        <>
            <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                <AgGridCustomTable
                    key={remountTable}
                    rowModelType="serverSide"
                    serverSideDatasource={serverSideDatasource}
                    columnDefs={columnDefs}
                    toolbar={toolbar}
                    defaultColDef={defaultColDef}
                    singleClickEdit={true}
                    onFirstDataRendered={onFirstDataRendered}
                    onGridSizeChanged={onGridSizeChanged}
                    onPaginationChanged={(event) => handlePageChanged(event.api.paginationGetCurrentPage())}
                />

                <Box sx={{display: "flex", mt: theme.spacing(2)}}>
                    <Button onClick={handleAddHoliday}>{t("msHoliday.labels.addHoliday")}</Button>
                </Box>

                <HolidayEditDialog
                    title={t("msHoliday.labels.addHoliday")}
                    open={showHolidayCreation}
                    onClose={handleCreationClose}
                    onSave={handleCreationSave}
                    isNew={true}
                    rowIndex={-1}
                    defaultTenant={formTenant}
                />
            </Paper>
            <CustomLoader loading={holidaysMutation.isLoading || holidayCreationMutation.isLoading || holidayEditMutation.isLoading} />
        </>
    )
}

export default HolidayTable